import React, { useEffect, useMemo, useRef } from 'react';
import PhotoTile from '../PhotoTile';
import { subscribeToScroll } from '../../../lib/gallery/scrollLoop';
import { usePrefersReducedMotion } from '../../../lib/gallery/usePrefersReducedMotion';
import type { PhotoItem } from '../../../lib/photo-sources/types';

/**
 * Balanced column masonry where each column drifts at a slightly different rate, so the grid
 * breathes as you scroll instead of moving as one slab.
 *
 * Parallax is measured per column rather than off page scroll: with hundreds of photos the page
 * is tens of thousands of pixels tall, and any factor applied to raw scrollY either does nothing
 * or flings a column into orbit.
 */

interface DepthMasonryProps {
  items: PhotoItem[];
  columnWidth: number;
  columnCount: number;
  gap: number;
  /** Same packing with the drift switched off — the control case for judging the parallax. */
  flat?: boolean;
  onOpen?: (index: number) => void;
}

/** Per-column drift in px across the full scroll of that column. Alternating signs read as depth. */
const DRIFT = [-42, 22, -28, 34];

interface ColumnProps {
  entries: Array<{ item: PhotoItem; index: number }>;
  columnWidth: number;
  gap: number;
  drift: number;
  onOpen?: (index: number) => void;
}

const ParallaxColumn: React.FC<ColumnProps> = ({ entries, columnWidth, gap, drift, onOpen }) => {
  // The transform lives on the inner element while measurements come from the outer one.
  // Measuring a transformed element would feed its own translation back into the next frame.
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const reduceMotion = usePrefersReducedMotion();

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || drift === 0 || reduceMotion) return;

    // Geometry is cached and refreshed only when the column actually resizes.
    //
    // Reading getBoundingClientRect() inside the scroll callback — after a previous column had
    // already written its transform — forced a synchronous layout of the whole tile tree, once
    // per column, every frame. That was the single most expensive thing on the page.
    let top = 0;
    let height = 0;

    const measure = () => {
      const rect = outer.getBoundingClientRect();
      top = rect.top + window.scrollY;
      height = rect.height;
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(outer);
    window.addEventListener('resize', measure, { passive: true });

    const unsubscribe = subscribeToScroll((scrollY) => {
      const viewport = window.innerHeight;
      const span = height + viewport;
      if (span <= 0) return;

      // 0 when the column's top meets the bottom of the viewport, 1 when its bottom clears the
      // top. Pure arithmetic — no DOM reads, so nothing can force a layout.
      const relativeTop = top - scrollY;
      const progress = Math.min(Math.max((viewport - relativeTop) / span, 0), 1);
      const offset = drift - progress * drift * 2;
      inner.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    });

    return () => {
      unsubscribe();
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [drift, reduceMotion]);

  return (
    <div ref={outerRef} className="min-w-0 flex-1">
      <div
        ref={innerRef}
        className="flex flex-col"
        style={{ gap, willChange: drift !== 0 && !reduceMotion ? 'transform' : undefined }}
      >
        {entries.map(({ item, index }) => (
          <PhotoTile
            key={item.id}
            item={item}
            columnWidth={columnWidth}
            onOpen={onOpen ? () => onOpen(index) : undefined}
          />
        ))}
      </div>
    </div>
  );
};

const DepthMasonry: React.FC<DepthMasonryProps> = ({
  items,
  columnWidth,
  columnCount,
  gap,
  flat = false,
  onOpen,
}) => {
  // Greedy shortest-column packing. Heights are estimated from aspect ratio, which is exact
  // here because every tile is column-width — no measurement pass needed.
  const columns = useMemo(() => {
    const buckets: Array<Array<{ item: PhotoItem; index: number }>> = Array.from(
      { length: columnCount },
      () => []
    );
    const heights = new Array<number>(columnCount).fill(0);

    items.forEach((item, index) => {
      let shortest = 0;
      for (let i = 1; i < columnCount; i++) {
        if (heights[i] < heights[shortest]) shortest = i;
      }
      buckets[shortest].push({ item, index });
      heights[shortest] += item.height / item.width; // normalized: height per unit width
    });
    return buckets;
  }, [items, columnCount]);

  return (
    <div className="flex w-full items-start" style={{ gap }}>
      {columns.map((entries, i) => (
        <ParallaxColumn
          key={i}
          entries={entries}
          columnWidth={columnWidth}
          gap={gap}
          drift={flat || columnCount === 1 ? 0 : DRIFT[i % DRIFT.length]}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
};

export default DepthMasonry;
